import {
    type ComboboxItem,
    type ComboboxParsedItemGroup,
    Select,
    type SelectProps,
    Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import type { Except, Merge, SetRequired } from 'type-fest';
import { filterArrayByString } from '../../../utils/utilFunctions';
import type { Controlled_CommonInputProps } from './types';

const perPage = 50;
const loadMore = 'Load more...';

export type Controlled_ReferenceInputProps<N extends boolean> = Merge<
    Except<
        SetRequired<SelectProps, 'data' | 'readOnly'>,
        | 'w'
        | 'searchable'
        | 'allowDeselect'
        | 'maxDropdownHeight'
        | 'comboboxProps'
        | 'rightSectionPointerEvents'
        | 'nothingFoundMessage'
        | 'limit'
        | 'renderOption'
        | 'filter'
        | 'dropdownOpened'
        | 'onDropdownOpen'
        | 'onDropdownClose'
    >,
    Controlled_CommonInputProps<N, string>
>;

export const Controlled_ReferenceInput = <N extends boolean>({
    nullable,
    onChange,
    placeholder,
    data,
    ...props
}: Controlled_ReferenceInputProps<N>) => {
    const [limit, setLimit] = useState(perPage);
    const [opened, { open, close }] = useDisclosure(false);

    const options = useMemo(() => {
        if (opened) {
            return data;
        }

        if (!props.value) {
            return [];
        }

        const selectedOption = data.find((o) => {
            const checkStringOrComboboxItem = (
                option: string | ComboboxItem
            ): boolean => {
                if (typeof option === 'string') {
                    return option === props.value;
                } else {
                    return option.value === props.value;
                }
            };
            if (typeof o === 'string' || 'value' in o) {
                return checkStringOrComboboxItem(o);
            } else {
                return Boolean(o.items.find(checkStringOrComboboxItem));
            }
        });

        return selectedOption ? [selectedOption] : [];
    }, [opened, data, props.value]);

    return (
        <Select
            {...props}
            data={options}
            dropdownOpened={opened}
            onDropdownOpen={open}
            onDropdownClose={() => {
                // @ts-expect-error to revalidate forms
                props.onBlur?.();
                close();
            }}
            w={'100%'}
            searchable={true}
            allowDeselect={false}
            maxDropdownHeight={200}
            comboboxProps={{ zIndex: 9000 }}
            rightSectionPointerEvents={'all'}
            nothingFoundMessage={'No options'}
            placeholder={placeholder ?? 'not defined'}
            limit={limit}
            onChange={onChange as ((value: string | null) => void) | undefined}
            renderOption={({ option }) =>
                option.value === loadMore ?
                    <Text
                        w={'calc(100% + 20px)'}
                        my={-6}
                        mx={-10}
                        py={6}
                        px={10}
                        size={'sm'}
                        onClick={() =>
                            setLimit((prevValue) => prevValue + perPage)
                        }
                    >
                        {option.label}
                    </Text>
                :   option.label
            }
            filter={({ options, limit, search }) => {
                const firstOption = options.at(0);
                if (!firstOption) {
                    return [];
                }

                if (!('group' in firstOption)) {
                    const filteredOptions = filterArrayByString(
                        search,
                        options as Array<ComboboxItem>,
                        ({ label }) => label
                    );
                    return filteredOptions.length <= limit ?
                            filteredOptions
                        :   [
                                ...filteredOptions.slice(0, limit),
                                {
                                    label: `${loadMore} (${limit}/${filteredOptions.length})`,
                                    value: loadMore,
                                    disabled: true,
                                },
                            ];
                }

                return (options as Array<ComboboxParsedItemGroup>).map(
                    ({ group, items }) => {
                        const filteredOptions = filterArrayByString(
                            search,
                            items,
                            ({ label }) => label
                        );
                        const groupLimit = Math.floor(limit / options.length); // TODO limiting could be more sophisticated

                        return {
                            group,
                            items:
                                filteredOptions.length <= groupLimit ?
                                    filteredOptions
                                :   [
                                        ...filteredOptions.slice(0, groupLimit),
                                        {
                                            label: `${loadMore} (${groupLimit}/${filteredOptions.length})`,
                                            value: loadMore,
                                            disabled: true,
                                        },
                                    ],
                        };
                    }
                );
            }}
        />
    );
};
